import { createAsyncThunk } from "@reduxjs/toolkit";

import * as basketAPI from "../../../api/basket";
import { IBasket } from "../../../models/catalogue";
import { IProductAPIURL } from "../../../models/nominals";
import { reloadBasket } from "./reloadBasket";

/**
 * Arguments for addBasketLine thunk.
 */
interface AddBasketLineArgs {
    productURL: IProductAPIURL;
    qty?: number;
}

/**
 * Add a product to the basket.
 *
 * This thunk adds a single product to the user's basket and reloads
 * the basket to reflect the updated state. This is the primary method
 * for adding products from product pages, configurators, and gift selections.
 *
 * @param {IProductAPIURL} args.productURL - API URL of the product to add
 * @param {number} [args.qty=1] - Quantity to add (defaults to 1)
 *
 * @example
 * // Add one product to basket
 * dispatch(addBasketLine({ productURL: product.url }));
 *
 * @example
 * // Add multiple quantities
 * dispatch(addBasketLine({ productURL: product.url, qty: 3 }));
 *
 * @sideEffects
 * - Makes API call to add product to basket via basketAPI.addProduct
 * - Dispatches reloadBasket thunk to reload basket data
 * - Updates `data.basket` with reloaded basket via extraReducers
 */
export const addBasketLine = createAsyncThunk(
    "basket/addBasketLine",
    async (args: AddBasketLineArgs, { dispatch }): Promise<IBasket | null> => {
        await basketAPI.addProduct(args.productURL, args.qty);
        const { basket } = await dispatch(reloadBasket()).unwrap();
        return basket;
    },
);
